import { request } from "../api";

export type EnrollmentMethod = "qr" | "invite" | "code";

export interface EnrollmentBatch {
  id: string;
  method: EnrollmentMethod;
  label: string;
  max_uses: number | null;
  expires_at: string | null;
  is_revoked: boolean;
  created_at: string;
  updated_at: string;
  tokens_total: number;
}

export interface CreateBatchInput {
  method: EnrollmentMethod;
  label?: string;
  max_uses?: number | null;
  expires_at?: string | null;
}

export interface EnrollmentTokenSummary {
  id: string;
  short_code: string;
  expires_at: string | null;
  consumed_at: string | null;
  consumed_by_device: string | null;
  user: string | null;
  user_email: string | null;
  user_full_name: string | null;
  created_at: string;
}

export interface IssuedToken {
  id: string;
  raw_token: string;
  short_code: string;
  enrollment_url: string;
  qr_payload: {
    v: number;
    api: string;
    token: string;
    company_slug: string;
  };
  expires_at: string | null;
}

export interface IssueTokensInput {
  count: number;
  user_id?: string | null;
  expires_at: string;
}

interface PaginatedList<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

function unwrapList<T>(payload: PaginatedList<T> | T[]): T[] {
  if (Array.isArray(payload)) return payload;
  return payload.results ?? [];
}

export const enrollmentApi = {
  listBatches: async (): Promise<EnrollmentBatch[]> => {
    const data = await request<PaginatedList<EnrollmentBatch> | EnrollmentBatch[]>(
      "/api/admin/v1/enrollment-batches/",
    );
    return unwrapList(data);
  },

  createBatch: (input: CreateBatchInput) =>
    request<EnrollmentBatch>("/api/admin/v1/enrollment-batches/", {
      method: "POST",
      json: input,
    }),

  getBatch: (id: string) =>
    request<EnrollmentBatch>(`/api/admin/v1/enrollment-batches/${id}/`),

  revokeBatch: (id: string) =>
    request<EnrollmentBatch>(`/api/admin/v1/enrollment-batches/${id}/revoke/`, {
      method: "POST",
    }),

  listTokens: (id: string) =>
    request<EnrollmentTokenSummary[]>(
      `/api/admin/v1/enrollment-batches/${id}/tokens/`,
    ),

  issueTokens: (id: string, input: IssueTokensInput) =>
    request<{ tokens: IssuedToken[] }>(
      `/api/admin/v1/enrollment-batches/${id}/tokens/`,
      {
        method: "POST",
        json: input,
      },
    ),
};
